import React, { useEffect } from 'react';

interface SmoothScrollProps {
  offset?: number;
  duration?: number;
}

const SmoothScroll: React.FC<SmoothScrollProps> = ({ 
  offset = 80, // Default offset to account for fixed header
  duration = 800 
}) => {
  useEffect(() => {
    // Function to handle smooth scrolling
    const handleSmoothScroll = (e: MouseEvent) => {
      // Check if the clicked element is an anchor link
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      
      if (!anchor) return;
      
      // Get the target element
      const targetId = anchor.getAttribute('href')?.substring(1);
      if (!targetId) return;
      
      const targetElement = document.getElementById(targetId);
      if (!targetElement) return;
      
      e.preventDefault();
      
      // Calculate the target position with offset
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();
      
      // Animation function
      const scrollStep = (timestamp: number) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth scrolling
        const easeInOutCubic = (t: number) => 
          t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        
        window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));
        
        if (progress < 1) {
          window.requestAnimationFrame(scrollStep);
        }
      };
      
      window.requestAnimationFrame(scrollStep);
    };
    
    // Add event listener
    document.addEventListener('click', handleSmoothScroll);
    
    // Clean up
    return () => {
      document.removeEventListener('click', handleSmoothScroll);
    };
  }, [offset, duration]);
  
  // This component doesn't render anything
  return null;
};

export default SmoothScroll; 